const get = require('lodash/get')

// routeCreatedAt is the creation date of the route a shipment is on, copied
// onto the CouchDB order and snapshot docs because a driver's offline device
// has no route doc to read it from (issue #25744).
//
// The source is avocado.data_route.created_at. For routes that start life in
// CouchDB that column is a copy of the doc's own createdAt (see
// tools/to-relational-model), so a producer holding a freshly saved route doc
// and a reader querying Postgres agree on the value.
//
// The one rule every writer has to keep: routeCreatedAt is only ever written
// together with the routeId it belongs to. A date left behind under a new
// route is worse than no date at all — it is a wrong date, and it spreads to
// every snapshot the order goes on to create.
const toRouteCreatedAt = createdAt => {
  if (!createdAt) {
    return null
  }
  // toJSON answers null for an unparseable date rather than throwing, and a
  // silent null here is indistinguishable from a route that has no date, so
  // say so.
  const isoDate = new Date(createdAt).toJSON()
  if (!isoDate) {
    console.warn(`Cannot read "${createdAt}" as a route creation date, storing no routeCreatedAt`)
  }
  return isoDate
}

// Reads the date for a route from Postgres.
//
// Resolves to null when there is no route to read a date from: the shipments
// are being taken off one, so they keep no date either.
//
// Resolves to undefined — "not told", the value resolveRouteCreatedAt leaves a
// doc's own date alone for — when the read cannot answer: no PG-backed routes
// api, or a route Postgres does not have (a 404). A route saved in CouchDB
// reaches data_route asynchronously, and the bulk upload screen stamps its date
// from the route doc in the meantime, so a 404 is not proof that no date was
// ever stamped from this route; answering null would wipe that date off the
// docs already on it.
//
// Every other failure rejects. A timeout or a dropped connection tells us
// nothing about the route's date, and writing the null we happen to be holding
// would erase a good value from docs that already carry one.
const readRouteCreatedAt = async (routesApi, routeId) => {
  if (!routeId) {
    return null
  }
  const adapter = get(routesApi, 'pgDataAdapter')
  if (!adapter) {
    return undefined
  }
  try {
    const route = await adapter.getOne(routeId, { whereCondition: 'couchdb_id' })
    return toRouteCreatedAt(route && route.created_at)
  } catch (err) {
    if (get(err, 'status') !== 404) {
      throw err
    }
    console.warn(`Route ${routeId} is not in Postgres, keeping the routeCreatedAt its docs carry`)
    return undefined
  }
}

// What a writer that is changing a doc's route should store as its date, given
// the date it was told to apply. `undefined` means "not told" — a message
// queued before routeCreatedAt existed, or a read that could not answer (see
// readRouteCreatedAt). Keeping the doc's own date is right while the route is
// unchanged, because that date was stamped from this same route elsewhere, but
// on an actual route change the date belongs to the route being left behind,
// so it is dropped.
const resolveRouteCreatedAt = (doc, routeId, routeCreatedAt) => {
  if (routeCreatedAt !== undefined) {
    return routeCreatedAt
  }
  return get(doc, 'routeId') === routeId ? (get(doc, 'routeCreatedAt') || null) : null
}

module.exports = { toRouteCreatedAt, readRouteCreatedAt, resolveRouteCreatedAt }
